import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useQuery, useMutation } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import produce from 'immer'
import { LoadingOutlined } from '@ant-design/icons'

import Footer from './Footer'
import QuizContent from './Content'
import { useLocalStorage } from '@/hooks'
import { parseImageURL, parseConditionalLogic } from '@prehook/shared'
import { theme } from '../../utils'

const QuizPageWrapper = styled.div`
  height: ${({ settings }) =>
    settings?.popupHeight ? settings.popupHeight + '%' : '100%'};
  width: ${({ settings }) =>
    settings?.popupWidth ? settings.popupWidth + '%' : '100%'};
`

const AnimatedPage = styled(motion.div)`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  display: flex;
  justify-content: center;
`

const PreviewMode = styled.div`
  display: flex;
  position: absolute;
  top: 0;
  right: 0;
  padding: 5px 10px;
  z-index: 2;
  border-radius: 5px;
  border: 1px solid ${theme.light.colors.purple};
  background-color: ${theme.light.colors.brightPurple};
  color: ${theme.light.colors.purple};
  font-size: 2rem;
  margin: 20px;
`

const QuizPage = ({ installType, draft }) => {
  const [imagesLoading, setImagesLoading] = useState(true)
  const [submissionId, setSubmissionId] = useState()
  const {
    data: { data: quiz },
  } = useQuery('quiz')

  // Choose correct shortId
  const shortId = draft ? quiz.shortId : quiz.pubShortId

  const createSubmissionReq = async () => {
    try {
      const body = {
        shortId,
        shop: quiz.store.shopifyId,
        timezone: quiz.store.timezone,
        totalFormFields: quiz.formFields.length,
      }
      const url = `${process.env.GATSBY_API_URL}/sub`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      })
      return response.json()
    } catch (error) {
      console.log(error)
    }
  }

  const [sub, setLocalSub] = useLocalStorage('sub', {})
  const [createSubmission] = useMutation(createSubmissionReq)

  const handleSetLocalSub = (formFieldId, answer, required) => {
    const newAnswer = { ...answer }
    // Need required for completedAt
    if (answer) {
      newAnswer.required = required
      setLocalSub(
        produce(sub, (draft) => {
          draft[shortId].answers[formFieldId] = newAnswer
        })
      )
    }
  }

  const cacheImages = async (srcArray) => {
    const promises = await srcArray.map((src) => {
      return new Promise(function (resolve, reject) {
        const img = new Image()

        img.src = src
        img.onload = resolve()
        img.onerror = reject()
      })
    })
    await Promise.all(promises)
    setImagesLoading(false)
  }

  const imgArray = (quiz) => {
    const images = []

    if (quiz.theme.backgroundImage) {
      const imageURL = parseImageURL({
        image: quiz.theme.backgroundImage,
        size: 'lrg',
        imageDomain: process.env.GATSBY_IMAGE_DOMAIN,
        bucketName: process.env.GATSBY_S3_BUCKET_NAME,
      })
      images.push(imageURL)
    }

    const formFields = quiz.formFields
    formFields.map((ff) => {
      if (ff.image) {
        const imageURL = parseImageURL({
          image: ff.image,
          size: 'med',
          imageDomain: process.env.GATSBY_IMAGE_DOMAIN,
          bucketName: process.env.GATSBY_S3_BUCKET_NAME,
        })
        images.push(imageURL)
      }
      if (ff.formFieldOptions) {
        ff.formFieldOptions.map((ffo) => {
          if (ffo.image) {
            const imageURL = parseImageURL({
              image: ffo.image,
              size: 'sml',
              imageDomain: process.env.GATSBY_IMAGE_DOMAIN,
              bucketName: process.env.GATSBY_S3_BUCKET_NAME,
            })
            images.push(imageURL)
          }
        })
      }
    })
    return images
  }

  const [direction, setDirection] = useState(0)
  const [allowNext, setAllowNext] = useState(true)
  const [selectedFormField, setSelectedFormField] = useState()
  const [ffHistory, setFFHistory] = useState([])

  const { formFields, formFieldIndex } = quiz
  const sortedFF = [...formFields].sort((a, b) => {
    return formFieldIndex.indexOf(a.id) - formFieldIndex.indexOf(b.id)
  })

  const updateFFHistory = ({ direction, formFieldId }) => {
    if (direction === -1) {
      setFFHistory(
        produce(ffHistory, (draft) => {
          draft.pop()
        })
      )
      return
    }
    setFFHistory(
      produce(ffHistory, (draft) => {
        draft.push(formFieldId)
      })
    )
  }

  useEffect(() => {
    const handleCreateSubmission = async () => {
      const res = await createSubmission()
      const submissionId = draft ? '' : res.data.submission.id
      setLocalSub(
        produce(sub, (draft) => {
          draft[shortId] = { subId: submissionId, answers: {} }
        })
      )
      setSubmissionId(submissionId)
    }

    if (!sub[shortId]) {
      handleCreateSubmission()
    }
    cacheImages(imgArray(quiz))
    updateFFHistory({ direction: 1, formFieldId: sortedFF[0]?.id })
  }, [])

  const selectedPage =
    sortedFF.length > 0
      ? sortedFF.findIndex((ff) => ff.id === selectedFormField?.id)
      : 0

  // Set selectedFormField to first question on initial load
  if (sortedFF.length > 0 && !selectedFormField) {
    setSelectedFormField(sortedFF[0])
  }

  const variants = {
    enter: (direction) => {
      return {
        zIndex: 0,
        y: direction > 0 ? 1000 : -1000,
        opacity: 0,
      }
    },
    center: {
      zIndex: 1,
      y: 0,
      opacity: 1,
    },
    exit: (direction) => {
      return {
        zIndex: 0,
        y: direction < 0 ? 1000 : -1000,
        opacity: 0,
      }
    },
  }

  const goToNextPage = () => {
    const newPage = selectedPage < sortedFF.length ? selectedPage + 1 : 0
    setSelectedFormField(sortedFF[newPage])
    setDirection(1)
    updateFFHistory({ direction: 1, formFieldId: sortedFF[newPage].id })
  }

  const goToPage = (formFieldId) => {
    const newFF = sortedFF.find((ff) => ff.id === formFieldId)
    setSelectedFormField(newFF)
    setDirection(1)
    updateFFHistory({ direction: 1, formFieldId })
  }

  const handleNextPage = (incomingAnswer) => {
    const { logicJump, id, fieldType } = selectedFormField

    if (fieldType === 'THANK_YOU') {
      return
    }

    if (!logicJump) {
      goToNextPage()
      return
    }

    const existingAnswers = sub[shortId]
    const { rules, fallback } = logicJump
    const nextFormFieldId = parseConditionalLogic({
      id,
      incomingAnswer,
      rules,
      existingAnswers,
    })

    if (!nextFormFieldId) {
      if (fallback?.next && fallback.next !== 'next_question')
        return goToPage(fallback.next)
      return goToNextPage()
    }

    if (nextFormFieldId === 'next_question') {
      return goToNextPage()
    }

    return goToPage(nextFormFieldId)
  }

  const paginate = (newDirection) => {
    if (newDirection === -1) {
      const lastFormFieldId = ffHistory[ffHistory.length - 2]
      const formField = sortedFF.find((ff) => ff.id === lastFormFieldId)
      setSelectedFormField(formField)
      updateFFHistory({ direction: newDirection })
    } else {
      // Go and parse the existing results again
      handleNextPage()
    }

    setDirection(newDirection)
  }

  const checkNotLastQuestion = () => {
    return selectedFormField.id !== sortedFF[sortedFF.length - 1].id
  }

  const buttonColor = quiz?.theme.buttonColor
  const installSettings = quiz?.installSettings
  const settings = installSettings?.[installType]

  if (imagesLoading) {
    return <LoadingOutlined />
  }

  return (
    <QuizPageWrapper id="form-container" settings={settings}>
      {draft && <PreviewMode>Preview Mode</PreviewMode>}
      <AnimatePresence initial={false} custom={direction}>
        <AnimatedPage
          key={selectedPage}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            // y: { type: 'spring', stiffness: 1000, damping: 500 },
            opacity: { duration: 0.2 },
          }}>
          {selectedFormField && (
            <QuizContent
              subId={submissionId}
              sub={sub[shortId]}
              handleLocalSub={handleSetLocalSub}
              formField={selectedFormField}
              quizName={quiz.name}
              integrations={quiz.integrations}
              nextPage={handleNextPage}
              setAllowNext={setAllowNext}
              draft={draft}
              history={ffHistory}
              shortId={shortId}
            />
          )}
        </AnimatedPage>
      </AnimatePresence>
      <Footer
        nextPage={() => paginate(1)}
        prevPage={() => paginate(-1)}
        allowPrev={selectedPage !== 0}
        allowNext={checkNotLastQuestion() && allowNext}
        color={buttonColor}
      />
    </QuizPageWrapper>
  )
}

QuizPage.propTypes = {
  installType: PropTypes.string,
}

export default QuizPage
